import AuthFormTabs from '@components/authentication/auth-form-tabs';
import createRedirect from '@components/create-redirect';
import { Spacer } from '@components/helpers';
import ModalCtaUnlockAccount from '@components/modals/ModalCtaUnlockAccount';
import PublicFAQ from '@components/PublicFAQ';
import SignIn from '@components/sign-in/sign-in';
import SignUp from '@components/sign-up/sign-up';
import { Grid } from '@devstart/react-bootstrap';
import { useLocation } from '@reach/router';
import { navigate } from 'gatsby-link';
import React, { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { createSelector } from 'reselect';
import { closeModalGoToUnlockPage } from '../redux/actions';
import {
  isModalGoToUnlockPageOpenSelector,
  isSignedInSelector
} from '../redux/selectors';

import './index.css';

const mapStateToProps = createSelector(
  isSignedInSelector,
  isModalGoToUnlockPageOpenSelector,
  (isSignedIn: boolean, isModalGoToUnlockPageOpen: boolean) => ({
    isSignedIn,
    isModalGoToUnlockPageOpen
  })
);

const mapDispatchToProps = {
  closeModalGoToUnlockPage
};

export interface IndexProps {
  readonly isSignedIn?: boolean;
  readonly isModalGoToUnlockPageOpen: boolean;
  readonly closeModalGoToUnlockPage: () => void;
}

interface UTMObject {
  [key: string]: string | null;
}

const RedirectLearn = createRedirect('/learn');

function IndexPage({
  isSignedIn,
  isModalGoToUnlockPageOpen,
  closeModalGoToUnlockPage
}: IndexProps): JSX.Element {
  const { t } = useTranslation();
  const location = useLocation();
  const [selectedTab, setSelectedTab] = useState(0);
  const faqRef = useRef<HTMLDivElement>(null);

  const scrollToFAQ = () => {
    faqRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  useEffect(() => {
    if (location.hash === '#perguntas-frequentes') {
      scrollToFAQ();
    }
  }, [location.hash]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          window.history.replaceState(null, '', '/#perguntas-frequentes');
        } else if (window.location.hash) {
          window.history.replaceState(null, '', window.location.pathname);
        }
      },
      { threshold: 0.1 }
    );

    if (faqRef.current) {
      observer.observe(faqRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const params = new URLSearchParams(location.search);
  const utmSource = params.get('utm_source');

  if (utmSource !== null) {
    const utmObject: UTMObject = {};
    params.forEach((value, key) => {
      utmObject[key] = value;
    });

    localStorage.setItem('devStart:utm', JSON.stringify(utmObject));
  }

  if (isSignedIn) {
    return <RedirectLearn />;
  }

  return (
    <>
      <Helmet title={`${t('register.title')} | DEVstart`} />
      <Grid className='index-container'>
        <Spacer size='large' />

        <AuthFormTabs
          selectedTab={selectedTab}
          setSelectedTab={setSelectedTab}
        />
        <Spacer size='medium' />

        <div className='sign-container'>
          {selectedTab === 0 ? <SignUp /> : <SignIn />}
        </div>

        <div className='faq-scroll-button-container'>
          <button onClick={scrollToFAQ} className='faq-scroll-button'>
            <span>Precisa de ajuda? Ver Perguntas Frequentes</span>
          </button>
        </div>
      </Grid>

      <Spacer size='exLarge' />

      <div ref={faqRef} className='public-faq-section'>
        <PublicFAQ />
      </div>

      <ModalCtaUnlockAccount
        show={isModalGoToUnlockPageOpen}
        onHide={() => closeModalGoToUnlockPage()}
        handleClick={() => {
          closeModalGoToUnlockPage();
          void navigate('/unlock-account');
        }}
      />
    </>
  );
}

IndexPage.displayName = 'IndexPage';

export default connect(mapStateToProps, mapDispatchToProps)(IndexPage);
